// VollmachtenKarte.tsx — Karte „Vollmachten": die Papiere, die Courté ÜBER
// den Kontakt hält (Kundenerstinformation, Maklervollmacht …).
//
// Zwei Modi (Plan docs/superpowers/plans/2026-09-27-kontakt-vollmachten.md §2.7):
//  - „anlage": kontrollierte Auswahl in der Kontaktanlage. Die Kundenerst-
//    information ist fest vorausgewählt; weitere per „+ Weitere Vollmacht
//    hinzufügen" (EintragsListe, wie „Weitere Anschrift"). Erzeugt wird nach
//    dem Speichern (`vollmachtenNachAnlage`).
//  - „akte": lädt die Vollmachten eines bestehenden Kontakts vom Server.
//    „Hochladen" öffnet dort die KMVS-Dokumentauswahl (Kunde fest, „Vom
//    Rechner" als Reiter) wie im Aktenkopf (T-42); nur die Anlage nimmt noch
//    direkt eine Datei aus dem Explorer — es gibt noch keine Akte.
//    Die Kontaktakte bindet nur den Inhalt ein (`VollmachtenAkteInhalt`) und
//    legt ihren eigenen Abschnittsrahmen (`Sec`) darum.
//
// Robust gegen einen Server ohne die neuen Routen (localhost gegen DEV): Die
// Auswahl funktioniert immer, Serveraktionen melden verständlich, dass die
// Funktion noch fehlt.

import * as React from "react";
import { mergeClasses } from "@fluentui/react-components";
import { SectionCard, LabeledSelect } from "../forms/formPrimitives";
import { EintragsListe } from "../forms/EintragsListe";
import { blobHerunterladen, vollmachtService, VollmachtFunktionFehlt } from "../../services/vollmachtService";
import { VollmachtAktionen, useVollmachtStyles } from "./VollmachtZeile";
import { SignierlinkDialog } from "./SignierlinkDialog";
import { VollmachtAuswahlDialog } from "../redesign/VollmachtAuswahlDialog";
import { auswahlHatInhalt, vollmachtAusAuswahlVerknuepfen } from "./vollmachtAusAuswahl";
import { unterschreibenGesperrt, unterschreibenLassen, type SignierlinkAnzeige } from "./signierlink";
import {
  freieOptionen,
  neueVollmachtUid,
  STANDARD_VOLLMACHT_UID,
  standardVollmachtAuswahl,
  vollmachtDateiname,
  vollmachtLabel,
  VOLLMACHT_OPTIONEN,
  type VollmachtAuswahl,
  type VollmachtDto,
  type VollmachtStatus,
  type VollmachtVorschauDaten,
} from "./vollmachtTypes";
import { VOLLMACHT_STANDARD_KEY } from "@kmvs/forms-core";
import {
  erstinfoJeKontakt,
  SAMMEL_LABEL,
  sammelMeldung,
  type SammelArt,
  type SammelMitglied,
} from "./erstinfoSammel";
import { sammelOptionText, useErstinfoSammel } from "./useErstinfoSammel";

export const HINWEIS_GENERIERT = "Wird bei der Erstellung für diesen Kontakt generiert";
export const HINWEIS_ERST_WAEHLEN = "Bitte zuerst eine Vollmacht wählen.";
export const HINWEIS_SIGNATUR_AUS =
  "Digitale Unterschrift noch nicht eingerichtet – der Link bietet das Dokument zum Herunterladen an.";

const fehlerText = (err: unknown): string =>
  err instanceof VollmachtFunktionFehlt
    ? err.message
    : `Das hat nicht geklappt: ${err instanceof Error ? err.message : String(err)}`;


/** Signaturstatus einmal je Karte laden; ohne Route gilt „aus". */
function useSignaturAktiv(): boolean {
  const [aktiv, setAktiv] = React.useState(false);
  React.useEffect(() => {
    let lebt = true;
    vollmachtService.signaturStatus().then((s) => lebt && setAktiv(s.enabled)).catch(() => undefined);
    return () => {
      lebt = false;
    };
  }, []);
  return aktiv;
}

// ── Modus „anlage" ──────────────────────────────────────────────────────

export type VollmachtenAnlageProps = {
  modus: "anlage";
  value: VollmachtAuswahl[];
  onChange: (next: VollmachtAuswahl[]) => void;
  /** Was die Maske schon über den Kontakt weiß — für die Vorschau-PDFs. */
  vorschauDaten: VollmachtVorschauDaten;
  /**
   * Haushalt (Privat) und Verbund (Firma: „Firmen-Verbindung zu", Privat:
   * „Verbindung zu Unternehmen"), wie im Formular erfasst. Nicht leer → neben
   * „Herunterladen" der Erstinformation „Für alle im … generieren".
   */
  sammel?: Partial<Record<SammelArt, SammelMitglied[]>>;
};

export type VollmachtenAkteProps = { modus: "akte"; contactId: number; nurLesen?: boolean };

export type VollmachtenKarteProps = VollmachtenAnlageProps | VollmachtenAkteProps;

export const VollmachtenKarte: React.FC<VollmachtenKarteProps> = (props) => (
  <SectionCard title="Vollmachten">
    {props.modus === "anlage" ? <AnlageInhalt {...props} /> : <AkteInhalt contactId={props.contactId} nurLesen={props.nurLesen} />}
  </SectionCard>
);

const AnlageInhalt: React.FC<VollmachtenAnlageProps> = ({ value, onChange, vorschauDaten, sammel }) => {
  const s = useVollmachtStyles();
  const signaturAktiv = useSignaturAktiv();
  const [meldung, setMeldung] = React.useState<Record<string, string>>({});
  const [laedt, setLaedt] = React.useState<string | null>(null);

  // Die Standardzeile ist immer da — auch wenn ein Aufrufer sie verliert.
  const standard = value.find((a) => a.uid === STANDARD_VOLLMACHT_UID) ?? standardVollmachtAuswahl()[0];
  const weitere = value.filter((a) => a.uid !== STANDARD_VOLLMACHT_UID);

  const aktuell = React.useRef(value);
  aktuell.current = value;
  const aendere = (uid: string, patch: Partial<VollmachtAuswahl>) => {
    const liste = aktuell.current.some((a) => a.uid === uid) ? aktuell.current : [standard, ...aktuell.current];
    onChange(liste.map((a) => (a.uid === uid ? { ...a, ...patch } : a)));
  };

  const herunterladen = async (eintrag: VollmachtAuswahl) => {
    if (!eintrag.art) {
      setMeldung((m) => ({ ...m, [eintrag.uid]: HINWEIS_ERST_WAEHLEN }));
      return;
    }
    setLaedt(eintrag.uid);
    setMeldung((m) => ({ ...m, [eintrag.uid]: "" }));
    try {
      const blob = await vollmachtService.vorschau(eintrag.art, vorschauDaten);
      blobHerunterladen(blob, vollmachtDateiname(eintrag.art, vorschauDaten.anzeigeName, " – Vorschau"));
    } catch (err) {
      setMeldung((m) => ({ ...m, [eintrag.uid]: fehlerText(err) }));
    } finally {
      setLaedt(null);
    }
  };

  // „Für alle im Haushalt/Verbund": je Kontakt eine PDF. Bestehende Mitglieder
  // bekommen sie gleich in ihre Akte; der neue Kontakt und frei eingetippte
  // sind noch nicht gespeichert — für sie nur die Vorschau.
  const [sammelLaeuft, setSammelLaeuft] = React.useState<SammelArt | null>(null);
  const [sammelText, setSammelText] = React.useState("");
  const sammelOptionen = (["haushalt", "verbund"] as const)
    .map((art) => ({ art, mitglieder: sammel?.[art] ?? [] }))
    .filter((o) => o.mitglieder.length > 0);
  const sammelStarten = async (art: SammelArt, mitglieder: SammelMitglied[]) => {
    setSammelLaeuft(art);
    setSammelText("");
    try {
      const ergebnisse = await erstinfoJeKontakt([
        { name: vorschauDaten.anzeigeName || "neuer Kontakt", vorschauDaten },
        ...mitglieder,
      ]);
      setSammelText(
        `${sammelMeldung(ergebnisse)} Die des neuen Kontakts wird beim Speichern abgelegt.`,
      );
    } catch (err) {
      setSammelText(fehlerText(err));
    } finally {
      setSammelLaeuft(null);
    }
  };
  const sammelKnoepfe = sammelOptionen.map((o) => (
    <button
      key={o.art}
      type="button"
      className={s.knopf}
      data-testid={`erstinfo-sammel-${o.art}`}
      disabled={!!sammelLaeuft}
      onClick={() => void sammelStarten(o.art, o.mitglieder)}
    >
      {sammelLaeuft === o.art ? "Wird erzeugt…" : sammelOptionText({ art: o.art, anzahl: o.mitglieder.length }, SAMMEL_LABEL[o.art])}
    </button>
  ));

  const unterschreibenTitel = signaturAktiv
    ? "Nach dem Speichern wird ein Link erzeugt, über den der Kontakt digital unterschreibt."
    : HINWEIS_SIGNATUR_AUS;

  const zeile = (eintrag: VollmachtAuswahl, bezeichnung: string, extra?: React.ReactNode) => (
    <>
      <VollmachtAktionen
        bezeichnung={bezeichnung}
        laedt={laedt === eintrag.uid}
        onHerunterladen={() => void herunterladen(eintrag)}
        onHochladen={(datei) => eintrag.art && aendere(eintrag.uid, { datei, signierenNachAnlage: false })}
        onUnterschreiben={() =>
          eintrag.art && aendere(eintrag.uid, { signierenNachAnlage: !eintrag.signierenNachAnlage })
        }
        unterschreibenAktiv={!!eintrag.signierenNachAnlage}
        unterschreibenTitel={unterschreibenTitel}
        // Ohne gewählte Art wüsste der Nach-Anlage-Schritt nicht, wohin mit
        // Datei oder Link — sie ginge still verloren.
        unterschreibenGesperrt={!!eintrag.datei || !eintrag.art}
        hochladenGesperrt={!eintrag.art}
        gesperrtTitel={!eintrag.art ? HINWEIS_ERST_WAEHLEN : undefined}
        zusatz={
          <>
          {extra}
          {eintrag.datei ? (
            <span className={mergeClasses(s.chip, s.chipOk)}>
              Datei gewählt: {eintrag.datei.name}
              <button
                type="button"
                className={s.chipX}
                aria-label={`Datei ${eintrag.datei.name} entfernen`}
                onClick={() => aendere(eintrag.uid, { datei: null })}
              >
                ✕
              </button>
            </span>
          ) : null}
          </>
        }
      />
      {eintrag.signierenNachAnlage && (
        <div className={s.info}>
          ✓ Link wird nach dem Speichern erzeugt{signaturAktiv ? "." : ` – ${HINWEIS_SIGNATUR_AUS}`}
        </div>
      )}
      {meldung[eintrag.uid] && (
        <div className={s.meldung} role="alert">
          {meldung[eintrag.uid]}
        </div>
      )}
    </>
  );

  const standardTitel = vollmachtLabel(VOLLMACHT_STANDARD_KEY);
  return (
    <div className={s.zeile}>
      <div className={s.titel}>{standardTitel}</div>
      <div className={s.hinweis}>{HINWEIS_GENERIERT}</div>
      {zeile(standard, standardTitel, sammelKnoepfe)}
      {sammelText && (
        <div className={s.info} role="status">
          {sammelText}
        </div>
      )}
      <EintragsListe<VollmachtAuswahl>
        items={weitere}
        onChange={(next) => onChange([standard, ...next])}
        neu={() => ({ uid: neueVollmachtUid(), art: "" })}
        hinzufuegenLabel="Weitere Vollmacht hinzufügen"
        renderFelder={(eintrag, update) => (
          <div className={s.zeile}>
            <LabeledSelect
              label="Vollmacht"
              name={`vollmacht-${eintrag.uid}`}
              value={eintrag.art}
              onChange={(art) => update({ art })}
              options={freieOptionen([standard, ...weitere], eintrag.uid)}
            />
            {zeile(eintrag, eintrag.art ? vollmachtLabel(eintrag.art) : "neue Vollmacht")}
          </div>
        )}
      />
    </div>
  );
};

// ── Modus „akte" ────────────────────────────────────────────────────────

const STATUS_TEXT: Record<VollmachtStatus, string> = {
  offen: "offen",
  generiert: "erzeugt",
  versendet: "Link aktiv",
  hochgeladen: "hochgeladen",
  unterschrieben: "unterschrieben",
};

export type VollmachtenAkteInhaltProps = {
  contactId: number;
  nurLesen?: boolean;
  /** Für Dateinamen und Meldung von „Für alle im … generieren". */
  kontaktName?: string;
  /** Bei einer Person: ihre Firmen („Verbindung zu Unternehmen") — zählen zum Verbund. */
  unternehmen?: Array<{ id: number; name: string }>;
};

const AkteInhalt: React.FC<VollmachtenAkteInhaltProps> = ({ contactId, nurLesen, kontaktName, unternehmen }) => {
  const s = useVollmachtStyles();
  const signaturAktiv = useSignaturAktiv();
  const [liste, setListe] = React.useState<VollmachtDto[] | null>(null);
  const [fehler, setFehler] = React.useState<string | null>(null);
  const [meldung, setMeldung] = React.useState<Record<number, string>>({});
  const [neueArt, setNeueArt] = React.useState("");
  const [signierlink, setSignierlink] = React.useState<SignierlinkAnzeige | null>(null);
  const signierlinkSchliessen = React.useCallback(() => setSignierlink(null), []);
  /** Vollmacht, für die gerade die Dokumentauswahl offen ist (T-42). */
  const [auswahlFuer, setAuswahlFuer] = React.useState<VollmachtDto | null>(null);
  const [sammelText, setSammelText] = React.useState("");
  const sammel = useErstinfoSammel(contactId, setSammelText, { name: kontaktName, unternehmen });

  const laden = React.useCallback(async () => {
    try {
      setListe(await vollmachtService.liste(contactId));
      setFehler(null);
    } catch (err) {
      setFehler(fehlerText(err));
    }
  }, [contactId]);
  React.useEffect(() => {
    void laden();
  }, [laden]);

  const aktion = async (v: VollmachtDto, fn: () => Promise<unknown>, erfolg?: string) => {
    setMeldung((m) => ({ ...m, [v.id]: "" }));
    try {
      await fn();
      if (erfolg) setMeldung((m) => ({ ...m, [v.id]: erfolg }));
      await laden();
    } catch (err) {
      setMeldung((m) => ({ ...m, [v.id]: fehlerText(err) }));
    }
  };

  if (fehler) return <div className={s.meldung} role="alert">{fehler}</div>;
  if (!liste) return <div className={s.info}>Lädt…</div>;

  const belegt = new Set(liste.map((v) => v.art));
  return (
    <div className={s.zeile}>
      {signierlink && <SignierlinkDialog links={[signierlink]} onClose={signierlinkSchliessen} />}
      {auswahlFuer && (
        <VollmachtAuswahlDialog
          contactId={contactId}
          kundeFest
          titel={`${auswahlFuer.titel} hinterlegen`}
          onClose={() => setAuswahlFuer(null)}
          onUebernehmen={(auswahl) => {
            const v = auswahlFuer;
            setAuswahlFuer(null);
            if (!auswahlHatInhalt(auswahl)) return;
            void aktion(v, () =>
              vollmachtAusAuswahlVerknuepfen({ contactId, art: v.art, auswahl, vollmacht: async () => v }),
            );
          }}
        />
      )}
      {liste.length === 0 && <div className={s.info}>Noch keine Vollmachten erfasst.</div>}
      {liste.map((v) => (
        <div key={v.id} className={s.zeile}>
          <div className={s.titel}>{v.titel}</div>
          <VollmachtAktionen
            bezeichnung={v.titel}
            onHerunterladen={() =>
              void aktion(v, async () => blobHerunterladen(await vollmachtService.pdf(contactId, v.id), vollmachtDateiname(v.art, kontaktName)))
            }
            // T-42: KMVS-Dokumentauswahl statt Windows-Explorer, wie im Aktenkopf.
            onHochladenAuswahl={() => setAuswahlFuer(v)}
            // Vorhandener gültiger Link → neues Fenster (synchron im Klick,
            // wegen Popup-Blocker); sonst neuer Link im eigenen Popup.
            onUnterschreiben={() =>
              void aktion(v, async () => {
                const anzeige = await unterschreibenLassen({
                  vollmacht: v,
                  titel: v.titel,
                  erzeugen: () => vollmachtService.signierlink(contactId, v.id),
                });
                if (anzeige) setSignierlink(anzeige);
              })
            }
            unterschreibenTitel={signaturAktiv ? undefined : HINWEIS_SIGNATUR_AUS}
            unterschreibenGesperrt={unterschreibenGesperrt(v, !nurLesen)}
            hochladenGesperrt={nurLesen}
            zusatz={
              <>
                <span
                  className={mergeClasses(
                    s.chip,
                    (v.status === "unterschrieben" || v.status === "hochgeladen") && s.chipOk,
                    v.status === "versendet" && s.chipGold,
                  )}
                >
                  {v.link?.abgelaufen ? "Link abgelaufen" : STATUS_TEXT[v.status]}
                  {v.link?.firstOpenedAt && !v.link.abgelaufen ? " · geöffnet" : ""}
                </span>
                {v.link && !nurLesen && (
                  <button type="button" className={s.knopf} onClick={() => void aktion(v, () => vollmachtService.widerrufen(contactId, v.id))}>
                    Link widerrufen
                  </button>
                )}
                {v.art === VOLLMACHT_STANDARD_KEY &&
                  sammel.optionen.map((o) => (
                    <button
                      key={o.art}
                      type="button"
                      className={s.knopf}
                      data-testid={`erstinfo-sammel-${o.art}`}
                      disabled={nurLesen || !!sammel.laeuft}
                      onClick={() => {
                        setSammelText("");
                        void sammel.starten(o.art).then(laden);
                      }}
                    >
                      {sammel.laeuft === o.art ? "Wird erzeugt…" : sammelOptionText(o, SAMMEL_LABEL[o.art])}
                    </button>
                  ))}
                {v.art !== VOLLMACHT_STANDARD_KEY && !nurLesen && (
                  <button type="button" className={s.knopf} onClick={() => void aktion(v, () => vollmachtService.entfernen(contactId, v.id))}>
                    Entfernen
                  </button>
                )}
              </>
            }
          />
          {v.link && !v.link.abgelaufen && <div className={s.link}>{v.link.url}</div>}
          {meldung[v.id] && <div className={s.info} role="status">{meldung[v.id]}</div>}
          {v.art === VOLLMACHT_STANDARD_KEY && sammelText && (
            <div className={s.info} role="status">
              {sammelText}
            </div>
          )}
        </div>
      ))}
      {!nurLesen && (
      <div className={s.aktionen}>
        <div style={{ minWidth: 280, flexGrow: 1 }}>
          <LabeledSelect
            label="Weitere Vollmacht"
            name={`vollmacht-neu-${contactId}`}
            value={neueArt}
            onChange={setNeueArt}
            options={VOLLMACHT_OPTIONEN.filter((o) => !belegt.has(o.value))}
          />
        </div>
        <button
          type="button"
          className={s.knopf}
          disabled={!neueArt}
          onClick={async () => {
            try {
              await vollmachtService.anlegen(contactId, [neueArt]);
              setNeueArt("");
              await laden();
            } catch (err) {
              setFehler(fehlerText(err));
            }
          }}
        >
          + Hinzufügen
        </button>
      </div>
      )}
    </div>
  );
};

/** Inhalt der Karte für die Kontaktakte — ohne eigenen Rahmen. */
export const VollmachtenAkteInhalt = AkteInhalt;

export default VollmachtenKarte;

// Einbindung in die Anlage-Formulare mit EINEM Import.
export { standardVollmachtAuswahl } from "./vollmachtTypes";
export type { VollmachtAuswahl } from "./vollmachtTypes";
export { vollmachtenNachAnlageMelden } from "./vollmachtenNachAnlage";

/** Anschrift einzeilig aus den Anlage-Feldern (für die Vorschau). */
export const anschriftEinzeilig = (strasse?: string, plz?: string, ort?: string): string =>
  [strasse?.trim(), [plz?.trim(), ort?.trim()].filter(Boolean).join(" ")].filter(Boolean).join(", ");
